import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import inputBgMd from "@assets/images/backgrounds/inputBgMd.webp";
import useApiHook from "@hooks/useApiHook";
import { UserContext, UserDispatchContext } from "@context/UserContext";
import TransparentLoader from "@components/Loaders/TransparentLoader";
import useApiError from "@hooks/useApiError";
import profileimg from "@assets/images/profile-dummy-user.webp";
import ChangePassword from "@components/ForgotPassword/ChangePassword";
import modalBorder from "@assets/images/borders/modalBorder.webp";
import { getToken } from "@src/utils/LoginHelper";
import useNotification from "@src/hooks/useNotification";
import ChangeEmail from "../ForgotEmail/ChangeEmail";
import VerifyEmail from "../ForgotEmail/VerifyEmail";
import useOnclickOutside from "react-cool-onclickoutside";
import { Helmet } from "react-helmet";
import { getValueOrEmpty } from "../../utils/ConditionHelper";
import instance from "@src/axios/axiosInstance";
import { AuthDispatchContext } from "@src/context/AuthContext";
import orderSummuryBorder from "@assets/images/borders/orderSummuryBorder.png";
import Cookies from "js-cookie";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { useMutation } from "@tanstack/react-query";
import { MutationType, MutationVariables } from "@src/types/Query";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { PhoneNumberUtil } from "google-libphonenumber";
import { countryCodes } from "@src/utils/CountryCodeHelper";
import { getImageUrlWithExpiry } from "@src/utils/ImageExpirtyHelper";
import useScrollRestoration from "@src/hooks/useScrollRestoration";
import { ChangeEmailContext } from "@src/context/ChangeEmailContext";
import type { IUser } from "@src/types/AppTypes";

const Account: React.FC = () => {
  const setAuth = useContext(AuthDispatchContext);
  const [error, setError] = useState<{
    type: "phoneNo" | "firstName" | "lastName";
    value: string;
  } | null>(null);
  const setApiError = useApiError();
  const [changepasswordModal, setChangePasswordModal] =
    useState<boolean>(false);
  const phoneRef = useRef<any>(null);
  const [changeEmailModal, setChangeEmailModal] = useState<boolean>(false);
  const [verifyemailModal, setVerifyEmailModal] = useState<boolean>(false);
  const user = useContext(UserContext);
  const userDispatch = useContext(UserDispatchContext);
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);
  const flashNotification = useNotification();
  const [currentEmail, setCurrentEmail] = useState<string>("");
  const [newEmail, setNewEmail] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("1");
  const [isUserProfileImageLoading, setIsUserProfileImageLoading] =
    useState<boolean>(true);
  // for close change email modal on outside click
  const [image, setImage] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const cropperRef = useRef<any>(null);
  const { setEmailData } = useContext(ChangeEmailContext);

  const changeEmailModalRef = useRef<HTMLDivElement>(null);
  useOnclickOutside(
    () => {
      document.getElementById("change-email-form")?.classList.add("form-shake");
    },
    { refs: [changeEmailModalRef], ignoreClass: ["swal2-container"] }
  );

  // for close change password modal on outside click
  const changePasswordModalRef = useRef<HTMLDivElement>(null);
  useOnclickOutside(
    () => {
      document
        .getElementById("change-password-form")
        ?.classList.add("form-shake");
    },
    { refs: [changePasswordModalRef], ignoreClass: ["swal2-container"] }
  );

  const updateProfileEndpoint = useApiHook({
    method: "post",
    url: "/users/edit_user",
  });

  const updateEmailEndPoint = useApiHook({
    method: "post",
    url: "/users/change_email",
  });

  const verifyEmailEndPoint = useApiHook({
    method: "post",
    url: "/users/save_changed_email",
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrop = () => {
    if (typeof cropperRef.current?.cropper !== "undefined") {
      setCroppedImage(
        cropperRef.current?.cropper.getCroppedCanvas().toDataURL()
      );
    }
  };

  const handleUpload = async () => {
    if (croppedImage) {
      const formData = new FormData();
      formData.append("user[profile]", dataURItoBlob(croppedImage));
      setImage(null);
      updateProfileImage({
        data: formData,
        headers: {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: getToken(),
          },
        },
      });
      // await updateProfileImage(formData);
    }
  };

  const dataURItoBlob = (dataURI: string): Blob => {
    const byteString = atob(dataURI.split(",")[1]);
    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const { mutate: updateProfileImage, isPending: updateProfileImagePending } =
    useMutation<
      MutationType<{
        message: string;
        status: boolean;
        user: IUser;
      }>,
      unknown,
      MutationVariables,
      unknown
    >({
      mutationKey: ["update-profile-image"],
      mutationFn: (v) =>
        updateProfileEndpoint(v.data, v.additionalArg, v.headers),
      onSettled: (data, error) => {
        if (data?.data.status) {
          if (user) {
            userDispatch({
              payload: { ...user, profile: data.data.user.profile },
              type: "updateUser",
            });
            setTimeout(() => {
              flashNotification(data.data);
            });
          }
        } else if (error) {
          setApiError(error);
        } else {
          flashNotification(data!.data);
        }
      },
    });

  const { mutateAsync: updateEmailMutate, isPending: updateEmailIsPending } =
    useMutation<
      MutationType<{
        message: string;
        status: boolean;
      }>,
      unknown,
      MutationVariables,
      unknown
    >({
      mutationKey: ["update-email"],
      mutationFn: (v) =>
        updateEmailEndPoint(v.data, v.additionalArg, v.headers),
      onSettled: (data, error, props) => {
        if (data?.data.status) {
          flashNotification(data.data);
          setCurrentEmail(props.data.current_email);
          setNewEmail(props.data.new_email);
          setChangeEmailModal(false);
          setVerifyEmailModal(true);
        } else if (error) {
          setApiError(error);
        } else {
          flashNotification(data!.data);
        }
      },
    });

  const { mutateAsync: verifyEmailMutate, isPending: verifyEmailIsPending } =
    useMutation<
      MutationType<{
        message: string;
        status: boolean;
      }>,
      unknown,
      MutationVariables,
      unknown
    >({
      mutationKey: ["verify-email"],
      mutationFn: (v) =>
        verifyEmailEndPoint(v.data, v.additionalArg, v.headers),
      onSettled: (data, error) => {
        if (data?.data.status) {
          flashNotification(data.data);

          setVerifyEmailModal(false);
          delete instance.defaults.headers.common["Authorization"];
          setEmailData({
            type: "changeEmail",
            payload: { email: "", password: "" },
          });
          Cookies.remove("aT");
          Cookies.remove("jwt_refresh_token");
          userDispatch({ type: "setUser", payload: null });
          setAuth({ type: "update", payload: false });
        } else if (error) {
          setApiError(error);
        } else {
          flashNotification(data!.data);
        }
      },
    });

  const isUserValid = useCallback(
    (firstName: string, lastName: string, phone_no: string) => {
      if (!firstName.length) {
        setError({ type: "firstName", value: "Please enter first name" });
        return false;
      } else if (!lastName.length) {
        setError({ type: "lastName", value: "Please enter last name" });
        return false;
      } else if (!phone_no.length) {
        setError({ type: "phoneNo", value: "Please enter phone number" });
        return false;
      } else if (phone_no.length < 7 || phone_no.length > 15) {
        setError({ type: "phoneNo", value: "Please enter valid phone number" });
        return false;
      }
      return true;
    },
    []
  );

  const {
    mutate: submitUserDetailsMutate,
    isPending: submitUserDetailsPending,
  } = useMutation<
    MutationType<{ message: string; status: boolean; user: IUser }>,
    unknown,
    MutationVariables,
    unknown
  >({
    mutationKey: ["submit-user-details"],
    mutationFn: (v) =>
      updateProfileEndpoint(v.data, v.additionalArg, v.headers),
    onSettled: (data, error) => {
      if (data?.data.status) {
        userDispatch({ type: "updateUser", payload: data.data.user });
        flashNotification(data.data);
      } else if (error) {
        setApiError(error);
      } else {
        flashNotification(data!.data);
      }
    },
  });

  const isSubmitButtonDisabled = useMemo(() => {
    if (!user || !currentUser) return true;
    if (
      user?.first_name !== currentUser?.first_name ||
      user?.last_name !== currentUser?.last_name ||
      (user?.country_code + user?.phone_no !==
        countryCode + currentUser.phone_no &&
        countryCode &&
        currentUser.phone_no)
    ) {
      return false;
    }
    return true;
  }, [currentUser, user, countryCode]);

  useEffect(() => {
    // Set the current user and country code only once when `user` changes
    if (user) {
      setCurrentUser(user);
      setCountryCode(user.country_code || "1");
    }
  }, [user]);

  useEffect(() => {
    // commented this for sheet issue no F71.
    // useEffect(() => {
    //   let timeout: NodeJS.Timeout | null;
    if (error) {
      if (error.type === "firstName") {
        document.getElementById("accountFirstName")?.focus();
      }
      if (error.type === "lastName") {
        document.getElementById("accountLastName")?.focus();
      }
      // if (error.type === "phoneNo") {
      //   phoneRef?.current?.focus();
      // }
      if (error.type === "phoneNo" && error.value) {
        phoneRef?.current?.focus();
      }
      //     timeout = setTimeout(() => {
      //       setError(null);
      //     }, 3000);
    }

    //   return () => {
    //     if (timeout) {
    //       clearTimeout(timeout);
    //     }
    //   };
  }, [error]);
  useEffect(() => {
    const header = document.getElementById("header");
    const footer = document.getElementById("footer");

    if (image) {
      document.body.style.overflow = "hidden";

      if (header) {
        header.style.zIndex = "0";
      }
      if (footer) {
        footer.style.zIndex = "-1";
      }
    }

    return () => {
      document.body.style.overflow = "auto";
      if (header) {
        header.style.zIndex = "";
      }
      if (footer) {
        footer.style.zIndex = "";
      }
    };
  }, [image]);

  useScrollRestoration();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <Helmet>
        <title>fatSu | Profile | Accounts</title>
        <meta
          name="description"
          content="Manage your personal account details including name, email, and password settings. Keep your profile updated to stay connected and secure."
        />
        <meta
          name="keywords"
          content="account details, user profile, personal info, update account, account management, profile settings"
        />
      </Helmet>
      <div className="relative px-14 py-10 max-sm:px-6 max-sm:py-8">
        {/* <img
          className="absolute top-0 left-0 size-full max-md:hidden"
          src={contactBorder}
          alt="account-contact-border"
        /> */}
        <img
          className="absolute top-0 left-0 size-full md:hidden"
          src={orderSummuryBorder}
          alt="account-contact-border"
        />
        <div className="relative">
          {submitUserDetailsPending && <TransparentLoader />}
          <h2 className="text-primary text-2xl mb-6 max-md:mb-5 max-md:text-2xl">
            Account details
          </h2>
          <div className="max-w-md mx-auto max-md:mb-12">
            <div className="text-center w-full col-span-2 mb-6 max-md:mb-4">
              <div className="relative inline-flex">
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  id="file-input"
                  onChange={async (e) => {
                    handleImageChange(e);
                  }}
                />
                {(isUserProfileImageLoading || updateProfileImagePending) && (
                  <div className="skeleton w-40 h-40 !rounded-full absolute"></div>
                )}
                <img
                  className="w-40 h-40 rounded-full border-4 border-primary object-cover"
                  src={
                    user?.profile
                      ? getImageUrlWithExpiry(user.profile)
                      : profileimg
                  }
                  onLoad={() => setIsUserProfileImageLoading(false)}
                  onError={() => setIsUserProfileImageLoading(false)}
                  alt="profile img"
                />
                {updateProfileImagePending && (
                  <div className="size-full flex items-center justify-center absolute">
                    <p className="video-loader"></p>
                  </div>
                )}
                <button
                  onClick={() => document.getElementById("file-input")?.click()}
                  disabled={updateProfileImagePending}
                  className="absolute bottom-0 right-0 w-16 h-16 bg-blue text-primary border-4 border-primary rounded-full grid place-items-center duration-150 hover:scale-95"
                  aria-label="camera-line"
                >
                  <i className="text-2xl ri-camera-line"></i>
                </button>

                <div className="modal-content">
                  {image && (
                    <div className="fixed size-full top-0 left-0 bg-black/50 z-[999] grid place-items-center modal-content">
                      <div className="bg-blue border-2 border-white rounded-lg overflow-hidden">
                        <Cropper
                          src={image}
                          className="max-sm:w-80 w-96 max-sm:h-80 h-96"
                          aspectRatio={1}
                          // initialAspectRatio={16 / 9}
                          guides={false}
                          crop={handleCrop}
                          ref={cropperRef}
                          initialAspectRatio={1}
                          viewMode={1}
                          minCropBoxHeight={10}
                          minCropBoxWidth={10}
                          background={false}
                          responsive={true}
                          autoCropArea={1}
                          checkOrientation={false}
                        />
                        <div className="grid grid-cols-2 gap-1 items-center bg-white p-1">
                          <button
                            onClick={() => setImage(null)}
                            className="p-4 bg-secondary text-blue duration-200 hover:opacity-65"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleUpload}
                            className="p-4 bg-secondary text-blue duration-200 hover:opacity-65"
                          >
                            Upload
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <h4 className="text-primary text-[26px] max-sm:text-lg mt-3 break-word">
                {user && user.first_name + " " + user.last_name}
              </h4>
            </div>

            <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1 w-full mb-4">
              <div>
                <label
                  className="text-secondary text-base mb-2 block f-family-mochiy-pop"
                  htmlFor="accountFirstName"
                >
                  First Name *
                </label>
                <div className="relative">
                  <img
                    className="absolute top-0 left-0 size-full"
                    src={inputBgMd}
                    alt=""
                  />
                  <input
                    id="accountFirstName"
                    value={getValueOrEmpty(currentUser?.first_name)}
                    maxLength={25}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "NumpadEnter") {
                        e.preventDefault();
                        document.getElementById("accountLastName")?.focus();
                      }
                    }}
                    onChange={(e) => {
                      setError({ type: "firstName", value: "" });
                      if (currentUser) {
                        setCurrentUser({
                          ...currentUser,
                          first_name: e.target.value,
                        });
                      }
                    }}
                    className="relative p-5 z-[1] text-base text-black w-full bg-transparent f-family-mochiy-pop"
                    type="text"
                    placeholder="Enter first name"
                  />
                </div>
                <p className="text-red-500">
                  {error?.type === "firstName" && error.value}
                </p>
              </div>
              <div>
                <label
                  className="text-secondary text-base mb-2 block f-family-mochiy-pop"
                  htmlFor="accountLastName"
                >
                  Last Name *
                </label>
                <div className="relative">
                  <input
                    id="accountLastName"
                    value={getValueOrEmpty(currentUser?.last_name)}
                    maxLength={25}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "NumpadEnter") {
                        e.preventDefault();
                        document.getElementById("accountLastName")?.blur();
                        const first_name = getValueOrEmpty(
                          currentUser?.first_name
                        );
                        const last_name = getValueOrEmpty(
                          currentUser?.last_name
                        );
                        const phone_no = getValueOrEmpty(currentUser?.phone_no);
                        const country_code = getValueOrEmpty(
                          currentUser?.country_code
                        );
                        if (!isUserValid(first_name, last_name, phone_no))
                          return;
                        const formData = new FormData();
                        formData.append("user[first_name]", first_name);
                        formData.append("user[last_name]", last_name);
                        formData.append("user[phone_no]", phone_no);
                        formData.append("user[country_code]", country_code);

                        submitUserDetailsMutate({
                          data: formData,
                          headers: {
                            headers: {
                              "Content-Type": "multipart/form-data",
                              Authorization: getToken(),
                            },
                          },
                        });
                      }
                    }}
                    onChange={(e) => {
                      setError({ type: "lastName", value: "" });
                      if (currentUser) {
                        setCurrentUser({
                          ...currentUser,
                          last_name: e.target.value,
                        });
                      }
                    }}
                    className="relative p-5 z-[1] text-base text-black w-full bg-transparent f-family-mochiy-pop"
                    type="text"
                    placeholder="Enter last name"
                  />
                  <img
                    className="absolute top-0 left-0 size-full"
                    src={inputBgMd}
                    alt=""
                  />
                </div>
                <p className="text-red-500">
                  {error?.type === "lastName" && error.value}
                </p>
              </div>
              <div className="sm:col-span-2">
                <label
                  className="text-secondary text-base mb-2 block f-family-mochiy-pop"
                  htmlFor="accountPhone"
                >
                  Phone Number *
                </label>
                <div
                  className="country-code-selector relative z-[10]"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "NumpadEnter") {
                      e.preventDefault();
                      e.stopPropagation();

                      const phoneUtil = PhoneNumberUtil.getInstance();
                      if (!phoneRef.current.value) {
                        setError({
                          type: "phoneNo",
                          value: "Please enter phone number",
                        });
                        return;
                      }

                      const fullPhoneNumber = `+${
                        countryCode + currentUser?.phone_no
                      }`;

                      try {
                        const phoneNumber = phoneUtil.parseAndKeepRawInput(
                          fullPhoneNumber,
                          countryCode
                        );

                        if (!phoneUtil.isValidNumber(phoneNumber)) {
                          setError({
                            type: "phoneNo",
                            value: "Please enter a valid phone number",
                          });
                          return;
                        }
                      } catch (error) {
                        return;
                      }
                    }
                  }}
                >
                  <PhoneInput
                    ref={phoneRef}
                    className="relative p-5 z-[4] text-base text-black w-full bg-transparent f-family-mochiy-pop reactPhoneNumberInputField"
                    placeholder="Enter phone number"
                    value={`+${countryCode}${currentUser?.phone_no}`}
                    onChange={(value) => {
                      setError({ type: "phoneNo", value: "" });

                      // Regular expression to extract the country code and phone number
                      const countryCodePattern = new RegExp(
                        `^\\+(${Object.values(countryCodes).join("|")})(\\d*)$`
                      );
                      const match = value?.match(countryCodePattern);

                      if (match) {
                        const detectedCountryCode = match[1]; // Extract the country code
                        const phoneNumber = match[2]; // Extract the remaining phone number
                        // Update the country code state
                        setCountryCode(detectedCountryCode);

                        // Update the currentUser object
                        if (currentUser) {
                          setCurrentUser({
                            ...currentUser,
                            country_code: detectedCountryCode,
                            phone_no: phoneNumber,
                          });
                        }
                      } else if (value === "" && !countryCode) {
                        // If the value doesn't match, reset country code and phone number
                        setCountryCode("");
                        setCurrentUser({
                          ...currentUser!,
                          country_code: "",
                          phone_no: "",
                        });
                      }

                      // Handle case where value is undefined or empty
                      if (!value && value.startsWith("+")) {
                        setCountryCode("");
                        setCurrentUser({
                          ...currentUser!,
                          country_code: "",
                          phone_no: "",
                        });
                      }
                    }}

                    // onBlur={() => {
                    //   // Validate phone number on blur
                    //   const fullPhoneNumber = `+${countryCode}${currentUser?.phone_no}`;
                    //   if (currentUser?.phone_no && countryCode) {
                    //     // Add your own validation logic or library call here
                    //     const isValid = /^[+][9]\d{9,14}$/.test(
                    //       fullPhoneNumber
                    //     );
                    //     if (isValid) {
                    //       setError({ type: "phoneNo", value: "" });
                    //     } else {
                    //       setError({
                    //         type: "phoneNo",
                    //         value: "Invalid phone number",
                    //       });
                    //     }
                    //   } else {
                    //     setError({
                    //       type: "phoneNo",
                    //       value: "Phone number is required",
                    //     });
                    //   }
                    // }}
                  />

                  <img
                    className="absolute top-0 left-0 size-full"
                    src={inputBgMd}
                    alt=""
                  />
                </div>
                <p className="text-red-500">
                  {error?.type === "phoneNo" && error.value}
                </p>
              </div>
              <div className="sm:col-span-2">
                <label
                  className="text-secondary text-base mb-2 block f-family-mochiy-pop"
                  htmlFor="accountEmail"
                >
                  Email
                </label>
                <div className="relative">
                  <div className="relative group">
                    <input
                      disabled={true}
                      id="accountEmail"
                      value={user ? user.email : ""}
                      className="relative p-5 pr-28 max-lg:pr-5 z-[2] text-base text-black w-full bg-transparent f-family-mochiy-pop overflow-hidden whitespace-nowrap text-ellipsis inline-block"
                      type="email"
                      placeholder="Enter email"
                      autoCapitalize="none"
                      inputMode="email"
                    />
                    <div className="absolute hidden group-hover:block bg-secondary text-white f-family-mochiy-pop text-sm rounded py-1 px-2 -bottom-8 left-0 z-50 break-all">
                      {user ? user.email : ""}
                    </div>
                    <img
                      className="absolute top-0 left-0 size-full"
                      src={inputBgMd}
                      alt=""
                    />
                  </div>
                  <button
                    className="mx-auto text-2xl underline absolute right-3 top-2/4 z-[2] -translate-y-2/4 flex items-center max-lg:top-full max-lg:mt-3"
                    onClick={() => {
                      setEmailData({
                        type: "changeEmail",
                        payload: { email: "", password: "" },
                      });
                      setTimeout(() => {
                        setChangeEmailModal(true);
                      });
                    }}
                    aria-label="change-email"
                  >
                    <span className="text-base text-secondary">
                      change email
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 max-w-md mx-auto max-lg:mt-9">
            <button
              disabled={isSubmitButtonDisabled}
              onClick={(e) => {
                e.preventDefault();
                const phoneUtil = PhoneNumberUtil.getInstance();

                if (countryCode && currentUser?.phone_no) {
                  const fullPhoneNumber =
                    "+" + countryCode + currentUser.phone_no;

                  try {
                    const phoneNumber = phoneUtil.parseAndKeepRawInput(
                      fullPhoneNumber,
                      countryCode
                    );

                    if (!phoneUtil.isValidNumber(phoneNumber)) {
                      setError({
                        type: "phoneNo",
                        value: "Please enter a valid phone number",
                      });
                      return;
                    }
                  } catch (error) {
                    return;
                  }
                }

                if (currentUser?.phone_no && countryCode) {
                  const first_name = getValueOrEmpty(currentUser?.first_name);
                  const last_name = getValueOrEmpty(currentUser?.last_name);
                  const phone_no = getValueOrEmpty(currentUser?.phone_no);
                  const country_code = getValueOrEmpty(countryCode);
                  const formData = new FormData();
                  formData.append("user[first_name]", first_name);
                  formData.append("user[last_name]", last_name);
                  formData.append("user[phone_no]", phone_no);
                  formData.append("user[country_code]", country_code);
                  e.preventDefault();
                  submitUserDetailsMutate({
                    data: formData,
                    headers: {
                      headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: getToken(),
                      },
                    },
                  });
                } else {
                  setError({
                    type: "phoneNo",
                    value: "Please enter phone number",
                  });
                }
              }}
              type="submit"
              className="mx-auto customized-button w-full max-w-xs text-xl max-sm:text-lg"
              aria-label="save-button"
            >
              Save Changes
            </button>
            <button
              onClick={() => {
                setChangePasswordModal(true);
              }}
              className="mx-auto text-xl max-sm:text-lg text-primary underline"
              aria-label="change-password"
            >
              <span className="text-primary-gradient">change Password</span>
            </button>
          </div>
        </div>
      </div>
      {changepasswordModal && (
        <ChangePassword
          modalBorder={modalBorder}
          setChangePasswordModal={setChangePasswordModal}
          changepasswordModal={changepasswordModal}
          ref={changePasswordModalRef}
        />
      )}
      {changeEmailModal && (
        <ChangeEmail
          modalBorder={modalBorder}
          changeemailModal={changeEmailModal}
          setChangeEmailModal={setChangeEmailModal}
          updateEmailMutate={updateEmailMutate}
          isLoading={updateEmailIsPending}
          ref={changeEmailModalRef}
        />
      )}
      <VerifyEmail
        verifyemailModal={verifyemailModal}
        setVerifyEmailModal={setVerifyEmailModal}
        setChangeEmailModal={setChangeEmailModal}
        modalBorder={modalBorder}
        currentEmail={currentEmail}
        newEmail={newEmail}
        verifyEmailMutate={verifyEmailMutate}
        isLoading={verifyEmailIsPending}
      />
    </form>
  );
};

export default Account;
